/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {BaseAsset, Device, HVSRecord} from '@ion/core/src/types/entities';
import {isHistoryAvailable} from '../utils';

/**
 * Custom hook to determine if asset history is available.
 *
 * Checks if the given record's latest event time is within the last 180 days.
 * Returns a memoized result containing whether history should be disabled
 * and the appropriate tooltip text.
 * 
 * ```typescript
 *  import useIsHistoryAvailable from '@ion/dls/src/maps/hooks/useIsHistoryAvailable';
 * 
 *  const isHistoryAvailable = useIsHistoryAvailable(record);
 * 
 * ```
 *
 * @param record - The asset record (HVSRecord, BaseAsset, or Device) to evaluate.
 * @returns A boolean  value `true` if history is not available (older than threshold days
 */
export const useIsHistoryAvailable = (record: HVSRecord | BaseAsset | Device) => (
  useMemo(() =>
    isHistoryAvailable(record as any)
  , [record])
);

export default useIsHistoryAvailable;
